import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Navbar = ({ cartItemCount, onOpenCart }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Shop', href: '#shop' },
    { name: 'Origins', href: '#origins' },
    { name: 'Brew Guide', href: '#guide' }
  ];

  return (
    <nav 
      className={`fixed w-full z-40 transition-all duration-500 ${scrolled ? 'bg-moon-white/95 backdrop-blur-md shadow-sm py-4' : 'bg-transparent py-6 md:py-8'}`}
      aria-label="Main Navigation"
    >
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        
        {/* Mobile Menu Toggle */}
        <div className="md:hidden flex-1">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`transition-colors duration-300 p-2 -ml-2 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade ${scrolled ? 'text-gray-800' : 'text-moon-white'} hover:text-jade`}
            aria-expanded={mobileMenuOpen}
            aria-label="Toggle navigation menu"
          >
            <SafeIcon icon={mobileMenuOpen ? FiIcons.FiX : FiIcons.FiMenu} className="w-6 h-6" />
          </button>
        </div>

        {/* Desktop Links */}
        <div className="hidden md:flex flex-1 space-x-10 text-xs tracking-[0.2em] uppercase font-medium">
          {navLinks.map((link) => (
            <a 
              key={link.name} 
              href={link.href} 
              className={`transition-colors duration-300 hover:text-jade focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade focus-visible:ring-offset-4 rounded-sm px-1 ${scrolled ? 'text-gray-600' : 'text-moon-white/90'}`}
            >
              {link.name}
            </a>
          ))}
        </div>
        
        {/* Logo */}
        <div className={`text-3xl md:text-4xl font-serif tracking-wide font-semibold text-center flex-1 transition-colors duration-300 ${scrolled ? 'text-plum-dark' : 'text-moon-white'}`}>
          <a href="#" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade rounded-sm px-2">
            LunaBrew
          </a>
        </div>

        {/* Icons */}
        <div className={`flex flex-1 justify-end space-x-2 md:space-x-6 items-center transition-colors duration-300 ${scrolled ? 'text-gray-600' : 'text-moon-white/90'}`}>
          <button 
            className="p-2 rounded-full hover:text-jade transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade"
            aria-label="Search"
          >
            <SafeIcon icon={FiIcons.FiSearch} className="w-5 h-5" />
          </button>
          <button 
            className="hidden md:block p-2 rounded-full hover:text-jade transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade"
            aria-label="User Account"
          >
            <SafeIcon icon={FiIcons.FiUser} className="w-5 h-5" />
          </button>
          <button 
            onClick={onOpenCart}
            className="p-2 rounded-full hover:text-jade transition-colors relative group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jade"
            aria-label={`Shopping Cart, ${cartItemCount} items`}
          >
            <SafeIcon icon={FiIcons.FiShoppingBag} className="w-5 h-5" />
            {cartItemCount > 0 && (
              <span className="absolute top-0 right-0 bg-jade text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center group-hover:bg-plum-dark transition-colors border border-white">
                {cartItemCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <div 
        className={`md:hidden absolute top-full left-0 w-full bg-moon-white border-b border-tea-stone transition-all duration-500 ease-in-out shadow-lg ${mobileMenuOpen ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
        aria-hidden={!mobileMenuOpen}
      >
        <div className="px-6 py-4 flex flex-col space-y-2">
          {navLinks.map((link) => (
            <a 
              key={link.name} 
              href={link.href} 
              onClick={() => setMobileMenuOpen(false)}
              className="text-sm tracking-[0.2em] uppercase text-gray-700 hover:text-jade py-3 border-b border-tea-stone/50 focus-visible:outline-none focus-visible:bg-tea-stone/30 px-2 rounded-sm transition-colors"
            >
              {link.name}
            </a>
          ))}
          <a href="#" className="text-sm tracking-[0.2em] uppercase text-gray-700 hover:text-jade py-3 focus-visible:outline-none focus-visible:bg-tea-stone/30 px-2 rounded-sm transition-colors">
            Account
          </a>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;